import { determineChannelKey, parentHeader, isWholeArea } from '../../../shared/seatsioUtils';

export function selectedUnits(object) {
  return Math.max(1, Number(object.numSelected) || 1);
}

export function addSelectedObject(currentObjects, object) {
  return [...currentObjects.filter((o) => o.id !== object.id), object];
}

export function removeSelectedObject(currentObjects, object) {
  return currentObjects.filter((o) => o.id !== object.id);
}

export function applyDeselection(currentObjects, object) {
  return (Number(object.numSelected) || 0) > 0
    ? addSelectedObject(currentObjects, object)
    : removeSelectedObject(currentObjects, object);
}

const keyOf = (value) => (value == null ? '' : String(value));

export function pricedCategoryKeys(ticketTypes) {
  return new Set((ticketTypes || []).filter((tt) => tt.price_parent_id != null).map((tt) => keyOf(tt.seating_chart_category_key)));
}

export function sectionTicketType(ticketTypes, categoryKey) {
  const inCategory = (ticketTypes || []).filter((tt) => keyOf(tt.seating_chart_category_key) === keyOf(categoryKey));
  return inCategory.find((tt) => tt.price_parent_id == null) || inCategory[0];
}

function heldPicks(object, ticketTypes) {
  const categoryKey = keyOf(object.category?.key);
  const ids = new Set((ticketTypes || []).filter((tt) => keyOf(tt.seating_chart_category_key) === categoryKey).map((tt) => String(tt.id)));
  const fallback = keyOf(sectionTicketType(ticketTypes, categoryKey)?.id);
  const perType = Object.entries(object.selectionPerTicketType || {}).filter(([, units]) => Number(units) > 0);
  const picks = perType.length > 0
    ? perType.map(([held, units]) => ({ held, units: Number(units) }))
    : [{ held: keyOf(object.selectedTicketType), units: selectedUnits(object) }];
  return picks.map(({ held, units }) => ({ held, ticketType: ids.has(held) ? held : fallback, units }));
}

export function ticketTypeSelections(object, ticketTypes) {
  const byType = new Map();
  heldPicks(object, ticketTypes).forEach(({ ticketType, units }) => {
    byType.set(ticketType, (byType.get(ticketType) || 0) + units);
  });
  return [...byType.entries()].map(([ticketType, units]) => ({ ticketType, units }));
}

export function heldReleaseSpecs(object, ticketTypes, ticketTypeId, amount) {
  let left = amount;
  return heldPicks(object, ticketTypes)
    .filter((pick) => ticketTypeId == null || pick.ticketType === keyOf(ticketTypeId))
    .flatMap(({ held, units }) => {
      const take = Math.min(left, units);
      left -= take;
      if (take <= 0) { return []; }
      return [held && held !== 'NO_TICKET_TYPE' ? { label: object.label, amount: take, ticketType: held } : { label: object.label, amount: take }];
    });
}

export function groupMetaForObject(object) {
  const categoryKey = object.category?.key?.toString();
  const section = object.labels?.section;
  const row = object.labels?.parent;
  const own = object.labels?.own;

  if (object.objectType === 'Table') {
    return { key: `table|${object.label}`, name: [section, `Table ${own}`].filter(Boolean).join(' · '), categoryKey, section, row: null, ga: false, table: true };
  }
  if (object.objectType === 'GeneralAdmissionArea') {
    return { key: `ga|${categoryKey}`, name: object.category?.label || 'General Admission', categoryKey, section: null, row: null, ga: true, table: false };
  }

  const name = [section && `Sec ${section}`, row && `${parentHeader(object)} ${row}`].filter(Boolean).join(' · ') || object.category?.label || 'Selection';
  return { key: `${categoryKey}|${section || ''}|${row || ''}`, name, categoryKey, section, row, ga: false, table: false };
}

export function groupSelectedObjects(selectedObjects, ticketTypes) {
  const priced = pricedCategoryKeys(ticketTypes);
  const byKey = new Map();
  (selectedObjects || []).forEach((object) => {
    const meta = groupMetaForObject(object);
    const parts = priced.has(keyOf(meta.categoryKey))
      ? ticketTypeSelections(object, ticketTypes).map(({ ticketType, units }) => {
        const tt = ticketTypes.find((t) => String(t.id) === ticketType);
        const name = tt.name === meta.name ? meta.name : `${meta.name} · ${tt.name}`;
        return { meta: { ...meta, key: `${meta.key}|${ticketType}`, name, ticketTypeId: tt.id }, units };
      })
      : [{ meta, units: selectedUnits(object) }];
    parts.forEach(({ meta: partMeta, units }) => {
      const group = byKey.get(partMeta.key) || { ...partMeta, qty: 0, labels: [], wholeSizes: {}, allWhole: true };
      group.qty += units;
      group.labels.push(object.label);
      if (isWholeArea(object)) { group.wholeSizes[object.label] = Number(object.capacity); } else { group.allWhole = false; }
      byKey.set(partMeta.key, group);
    });
  });
  return [...byKey.values()].map(({ allWhole, wholeSizes, ...group }) => (
    allWhole ? { ...group, wholeSizes } : group
  ));
}

export function onSeatMap(ticketType) {
  return !!ticketType.seating_chart_category_key;
}

export function listedBesideSeatMap(ticketType) {
  return !onSeatMap(ticketType) && (ticketType.ancestor_ids || []).length > 1;
}

export function buildTicketTypesForSelection(
  ticketTypes,
  selectedObjects,
  { selectedObjectLabel, selectedObjectType, configuredChannels, priceOptions = ticketTypes }
) {
  const priced = pricedCategoryKeys(ticketTypes);
  return ticketTypes.map((tt) => {
    if (!onSeatMap(tt)) { return tt; }

    const pricedSection = priced.has(keyOf(tt.seating_chart_category_key));
    const filteredObjects = selectedObjects.filter(
      (o) => o.category.key.toString() === tt.seating_chart_category_key
    );

    const seatsIOObjects = filteredObjects.flatMap((object) => {
      const entry = {
        seatsio_object_id: object.id,
        seat_assignment: selectedObjectLabel(object),
        seat_type: selectedObjectType(object),
        hold_token: object.chart?.holdToken,
        channel_key: determineChannelKey(object, configuredChannels),
      };
      const units = pricedSection
        ? ticketTypeSelections(object, priceOptions).filter((pick) => pick.ticketType === String(tt.id)).reduce((sum, pick) => sum + pick.units, 0)
        : selectedUnits(object);
      return Array.from({ length: units }, () => ({ ...entry }));
    });

    return Object.assign({}, tt, {
      seatsIOObjects,
      quantity: seatsIOObjects.length,
    });
  });
}

export function keepSeatSelections(calculatedTicketTypes, currentTicketTypes) {
  const seatSelections = new Map(currentTicketTypes.filter((tt) => tt.seatsIOObjects).map((tt) => [tt.id, tt]));

  return calculatedTicketTypes.map((tt) => {
    const seated = seatSelections.get(tt.id);
    return seated ? { ...tt, quantity: seated.quantity, seatsIOObjects: seated.seatsIOObjects } : tt;
  });
}

const ENTITIES = { '&nbsp;': ' ', '&amp;': '&', '&lt;': '<', '&gt;': '>', '&quot;': '"', '&#39;': "'" };

export const plainText = (html) => (html || '')
  .replace(/<[^>]*>/g, ' ')
  .replace(/&(nbsp|amp|lt|gt|quot|#39);/gi, (entity) => ENTITIES[entity.toLowerCase()])
  .replace(/\s+/g, ' ')
  .trim();

const priceFields = (tt, promoPrices) => (promoPrices ? {
  price: parseFloat(promoPrices.calculated_price),
  originalPrice: parseFloat(tt.calculated_price)
} : {
  price: parseFloat(tt.calculated_price)
});

export function priceCaps(ticketTypes) {
  return (ticketTypes || []).flatMap((tt) => {
    if (tt.price_parent_id == null || !tt.maximum_tickets_during_checkout) { return []; }
    const section = sectionTicketType(ticketTypes, tt.seating_chart_category_key);
    const sectionCap = section && section !== tt ? section.maximum_tickets_during_checkout : null;
    return sectionCap && tt.maximum_tickets_during_checkout >= sectionCap ? [] : [{ tt, quantity: tt.maximum_tickets_during_checkout }];
  });
}

export function seatsioPriceLimits(ticketTypes) {
  const caps = priceCaps(ticketTypes);
  if (caps.length === 0) { return []; }
  const priced = pricedCategoryKeys(ticketTypes);
  return (ticketTypes || []).filter((tt) => priced.has(keyOf(tt.seating_chart_category_key))).map((tt) => {
    const cap = caps.find((c) => c.tt === tt);
    const section = sectionTicketType(ticketTypes, tt.seating_chart_category_key);
    return { ticketType: String(tt.id), quantity: cap ? cap.quantity : section.maximum_tickets_during_checkout };
  });
}

const capNote = (tt, ticketTypes) => {
  const cap = priceCaps(ticketTypes).find((c) => c.tt === tt);
  return cap ? `Max ${cap.quantity} ${tt.name} per order` : null;
};

const describe = (tt, ticketTypes) => [plainText(tt.simple_format_description), capNote(tt, ticketTypes)].filter(Boolean).join(' · ');

export function buildSeatsioPricing(ticketTypes, offeredTicketTypes, promoPricesFor) {
  const priced = pricedCategoryKeys(ticketTypes);
  const offeredIds = new Set((offeredTicketTypes || []).map((tt) => tt.id));
  const done = new Set();
  return ticketTypes.filter(onSeatMap).flatMap((tt) => {
    const categoryKey = keyOf(tt.seating_chart_category_key);
    if (!priced.has(categoryKey)) {
      return [{ category: tt.seating_chart_category_key, ...priceFields(tt, promoPricesFor(tt)) }];
    }
    if (done.has(categoryKey)) { return []; }
    done.add(categoryKey);
    const section = sectionTicketType(ticketTypes, categoryKey);
    const rows = ticketTypes
      .filter((row) => keyOf(row.seating_chart_category_key) === categoryKey)
      .sort((a, b) => (a === section ? -1 : b === section ? 1 : 0));
    return [{
      category: tt.seating_chart_category_key,
      ticketTypes: rows.map((row) => ({
        ticketType: String(row.id),
        label: row.name,
        ...priceFields(row, promoPricesFor(row)),
        ...(describe(row, ticketTypes) ? { description: describe(row, ticketTypes) } : {}),
        ...(offeredIds.has(row.id) ? {} : { unavailable: true, unavailableReason: 'unavailable' })
      }))
    }];
  });
}

export function minimumShortfalls(groups, ticketTypes) {
  const counts = new Map();
  (groups || []).filter((g) => !g.table).forEach((g) => counts.set(keyOf(g.categoryKey), (counts.get(keyOf(g.categoryKey)) || 0) + g.qty));
  return [...counts].flatMap(([categoryKey, picked]) => {
    const section = sectionTicketType(ticketTypes, categoryKey);
    const minimum = section ? Number(section.minimum_tickets_per_order) || 0 : 0;
    return picked < minimum ? [`${section.name} requires at least ${minimum} seats per order. You've picked ${picked}.`] : [];
  });
}
